"use client";

import { useDeferredValue, useState } from "react";
import {
  Checkbox,
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { UrlRebuilder } from "@/components/tools/latex-dev/url-rebuilder";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  paramsToObject,
  parseUrl,
  type QueryParam,
  type UrlNote,
  type UrlParts,
} from "@/lib/tool-kit/latex-dev/url-parts";

/** English strings. A translated content file overrides these through `ui`. */
export const URL_PARSER_STRINGS = {
  inputLabel: "URL to take apart",
  placeholder: "https://example.com/path?name=value#section",
  hint: "Parsed in your browser by the browser's own URL parser. Nothing is sent and the address is never opened.",
  assumeHttps: "Assume https:// when the scheme is missing",
  loadExample: "Load the example",
  clear: "Clear",
  example:
    "https://user:p%40ss@www.Example.com:8443/docs/./v2/../guide%20one/index.html?q=url+parser&tag=a&tag=b&utm_source=newsletter&page=2&empty=&flag#section=install&mode=dark",
  emptyPrompt: "Paste a URL, or load the example, to see its parts.",
  errors: {
    empty: "There is nothing to parse.",
    badPort:
      "The port is not valid. A port is a number from 0 to 65535 after a colon.",
    badHost:
      "The host name is missing or contains a character a host cannot have, such as a space.",
    badScheme:
      "This does not start with a scheme such as https:// and does not look like a host name.",
    invalid: "This is not a URL the browser can parse.",
  },
  results: "Parts",
  colPart: "Part",
  colValue: "Value",
  colNotes: "Notes",
  absent: "—",
  partNames: {
    scheme: "Scheme",
    username: "Username",
    password: "Password",
    host: "Host",
    port: "Port",
    path: "Path",
    query: "Query",
    fragment: "Fragment",
    origin: "Origin",
    href: "Normalised URL",
  },
  hostKinds: {
    domain: "domain name",
    ipv4: "IPv4 address",
    ipv6: "IPv6 address",
    none: "no host",
  },
  punycodeNote: "stored as {punycode}",
  labelsNote: "{labels}",
  portDefault: "not written; {scheme} uses {port}",
  segmentsNote: "one: {count} segment|other: {count} segments",
  paramsNote: "one: {count} parameter|other: {count} parameters",
  decodedNote: "decodes to {text}",
  segmentsHeading: "Path segments",
  colIndex: "#",
  colSegment: "Segment",
  colDecoded: "Decoded",
  paramsHeading: "Query parameters",
  hashParamsHeading: "Parameters in the fragment",
  colName: "Name",
  colParamValue: "Value",
  paramFlags: {
    repeated: "name repeats",
    tracking: "tracking parameter",
    noValue: "no value",
    malformed: "invalid encoding, shown as written",
  },
  copyJson: "Copy parameters as JSON",
  copyHref: "Copy normalised URL",
  copied: "Copied!",
  notesHeading: "What the parser did and noticed",
  notes: {
    assumedScheme: "No scheme was written, so https:// was assumed.",
    relative:
      "This is a relative reference: it has no scheme or host, only a path, query or fragment.",
    credentials:
      "The address contains a username or password. Browsers discourage this, and the text appears in logs and history.",
    schemeLowercased: "The scheme was converted to lower case.",
    hostNormalised:
      "The host name was converted to lower case (or to its ASCII form).",
    portRemoved:
      "The port was removed because it is the default for the scheme.",
    pathAdded:
      "A path of / was added, as browsers do for an address with no path.",
    dotSegments: "Dot segments (. and ..) in the path were resolved.",
    reEncoded: "Characters that cannot appear in a URL were percent-encoded.",
    backslashes: "Backslashes were read as forward slashes.",
    malformedEncoding:
      "A query parameter contains a percent sequence that is not valid UTF-8; it is shown as written.",
    internationalHost:
      "The host name contains non-ASCII characters; the browser stores it in its Punycode form.",
    noHost:
      "This scheme has no host, so everything after the scheme is the path.",
  },
  rebuildHeading: "Edit and rebuild",
  rebuildHint:
    "Change any part and the URL below is rebuilt. Parameter names and values are encoded for you.",
  paramName: "Name of parameter {n}",
  paramValue: "Value of parameter {n}",
  removeRow: "Remove",
  addRow: "Add a parameter",
  removeTracking: "Remove tracking parameters",
  sortRows: "Sort by name",
  spaceLabel: "Encode spaces as",
  spaceStyles: {
    percent: "%20",
    plus: "+",
  },
  rebuiltLabel: "Rebuilt URL",
  copyRebuilt: "Copy rebuilt URL",
  paramCount: "Parameters",
  segmentCount: "Path segments",
  length: "Characters",
} as const;

type Strings = typeof URL_PARSER_STRINGS;
type Fmt = ReturnType<typeof useToolNumber>;

const NOTE_ORDER: readonly UrlNote[] = [
  "assumedScheme",
  "relative",
  "credentials",
  "schemeLowercased",
  "hostNormalised",
  "internationalHost",
  "portRemoved",
  "pathAdded",
  "dotSegments",
  "backslashes",
  "reEncoded",
  "malformedEncoding",
  "noHost",
];

function dash(text: string, strings: Strings): string {
  return text === "" ? strings.absent : text;
}

function hostNote(parts: UrlParts, strings: Strings): string {
  const pieces: string[] = [strings.hostKinds[parts.hostKind]];
  if (parts.hostKind === "domain") {
    pieces.push(parts.labels.join(" · "));
    if (parts.unicodeHost !== parts.hostname) {
      pieces.push(t(strings.punycodeNote, { punycode: parts.hostname }));
    }
  }
  return parts.relative ? "" : pieces.join("; ");
}

function partRows(
  parts: UrlParts,
  strings: Strings,
  locale: string,
  fmt: Fmt
): string[][] {
  const names = strings.partNames;
  const rows: string[][] = [
    [names.scheme, dash(parts.scheme, strings), ""],
    [names.username, dash(parts.username, strings), ""],
    [names.password, dash(parts.password, strings), ""],
    [names.host, dash(parts.unicodeHost, strings), hostNote(parts, strings)],
    [
      names.port,
      dash(parts.port, strings),
      parts.portIsDefault
        ? t(strings.portDefault, {
            scheme: parts.scheme,
            port: parts.effectivePort,
          })
        : "",
    ],
    [
      names.path,
      dash(parts.pathname, strings),
      parts.segments.length > 0
        ? tPlural(strings.segmentsNote, locale, parts.segments.length, {
            count: fmt(parts.segments.length),
          }).trim()
        : "",
    ],
    [
      names.query,
      dash(parts.search, strings),
      parts.params.length > 0
        ? tPlural(strings.paramsNote, locale, parts.params.length, {
            count: fmt(parts.params.length),
          }).trim()
        : "",
    ],
    [
      names.fragment,
      dash(parts.hash, strings),
      parts.hash !== "" && parts.hashDecoded !== parts.hash.slice(1)
        ? t(strings.decodedNote, { text: parts.hashDecoded })
        : "",
    ],
    [names.origin, dash(parts.origin, strings), ""],
    [names.href, parts.href, ""],
  ];
  // Credentials and an origin are shown only when there are some.
  const optional = new Set<string>([
    names.username,
    names.password,
    names.origin,
  ]);
  return rows.filter(
    (row) => row[1] !== strings.absent || !optional.has(row[0])
  );
}

function paramFlags(param: QueryParam, strings: Strings): string {
  const flags: string[] = [];
  if (param.repeated) {
    flags.push(strings.paramFlags.repeated);
  }
  if (param.tracking) {
    flags.push(strings.paramFlags.tracking);
  }
  if (!param.hasEquals) {
    flags.push(strings.paramFlags.noValue);
  }
  if (param.malformed) {
    flags.push(strings.paramFlags.malformed);
  }
  return flags.join("; ");
}

function ParamTable({
  params,
  heading,
  strings,
  fmt,
}: {
  params: QueryParam[];
  heading: string;
  strings: Strings;
  fmt: Fmt;
}) {
  if (params.length === 0) {
    return null;
  }
  return (
    <>
      <h3 className={WIDGET_H3}>{heading}</h3>
      <DataTable
        head={[
          strings.colIndex,
          strings.colName,
          strings.colParamValue,
          strings.colNotes,
        ]}
        rows={params.map((p, i) => [
          fmt(i + 1),
          p.name,
          p.value,
          paramFlags(p, strings),
        ])}
      />
    </>
  );
}

export default function UrlParserWidget() {
  const strings = useToolUi(URL_PARSER_STRINGS);
  const [source, setSource] = useState("");
  const [assume, setAssume] = useState(true);
  const deferred = useDeferredValue(source);
  const result = deferred.trim() === "" ? null : parseUrl(deferred, assume);

  return (
    <section className="space-y-6">
      <TextAreaField
        hint={strings.hint}
        id="up-input"
        label={strings.inputLabel}
        onChange={setSource}
        placeholder={strings.placeholder}
        rows={4}
        value={source}
      />
      <Checkbox
        checked={assume}
        label={strings.assumeHttps}
        onChange={setAssume}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SMALL}
          onClick={() => setSource(strings.example)}
          type="button"
        >
          {strings.loadExample}
        </button>
        <button
          className={BUTTON_SMALL}
          onClick={() => setSource("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <div aria-live="polite" className="space-y-4">
        {result === null ? (
          <Notice>{strings.emptyPrompt}</Notice>
        ) : (
          <Results result={result} strings={strings} />
        )}
      </div>
    </section>
  );
}

function Results({
  result,
  strings,
}: {
  result: ReturnType<typeof parseUrl>;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  if (!result.ok) {
    return <ErrorNotice>{strings.errors[result.code]}</ErrorNotice>;
  }
  const { parts } = result;
  const json = JSON.stringify(paramsToObject(parts.params), null, 2);
  return (
    <>
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.paramCount}
          value={fmt(parts.params.length)}
        />
        <StatCard
          label={strings.segmentCount}
          value={fmt(parts.segments.length)}
        />
        <StatCard label={strings.length} value={fmt(parts.href.length)} />
      </StatGrid>
      <DataTable
        head={[strings.colPart, strings.colValue, strings.colNotes]}
        rows={partRows(parts, strings, locale, fmt)}
      />
      <div className="flex flex-wrap gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyHref}
          text={parts.href}
        />
        <CopyButton
          copiedLabel={strings.copied}
          disabled={parts.params.length === 0}
          label={strings.copyJson}
          text={json}
        />
      </div>
      {parts.segments.length > 0 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.segmentsHeading}</h3>
          <DataTable
            head={[strings.colIndex, strings.colSegment, strings.colDecoded]}
            rows={parts.segments.map((s, i) => [fmt(i + 1), s.raw, s.decoded])}
          />
        </>
      ) : null}
      <ParamTable
        fmt={fmt}
        heading={strings.paramsHeading}
        params={parts.params}
        strings={strings}
      />
      <ParamTable
        fmt={fmt}
        heading={strings.hashParamsHeading}
        params={parts.hashParams}
        strings={strings}
      />
      {parts.notes.length > 0 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.notesHeading}</h3>
          <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
            {NOTE_ORDER.filter((note) => parts.notes.includes(note)).map(
              (note) => (
                <li key={note}>{strings.notes[note]}</li>
              )
            )}
          </ul>
        </>
      ) : null}
      <UrlRebuilder
        fmt={fmt}
        key={parts.href}
        parts={parts}
        strings={strings}
      />
    </>
  );
}
